/**
 * Avisos sonoros e notificações do sistema operacional. Preferências por navegador
 * (localStorage): som escolhido entre os prontos (gerados na hora) ou um arquivo do usuário.
 */

export type SoundId = 'bipe' | 'sino' | 'gota' | 'duplo' | 'suave' | 'custom';

export interface NotifyPrefs {
  sound: boolean;
  desktop: boolean;
  soundId: SoundId;
  /** 0 a 1. */
  volume: number;
  /** Nome do arquivo personalizado (o conteúdo fica em outra chave). */
  customName: string | null;
}

export const SOUNDS: Array<{ id: SoundId; label: string }> = [
  { id: 'bipe', label: 'Bipe (padrão)' },
  { id: 'sino', label: 'Sino' },
  { id: 'gota', label: 'Gota' },
  { id: 'duplo', label: 'Toque duplo' },
  { id: 'suave', label: 'Suave' },
  { id: 'custom', label: 'Arquivo meu…' },
];

/** Arquivo de som personalizado: até 1 MB (fica guardado no navegador). */
export const MAX_CUSTOM_SOUND_BYTES = 1024 * 1024;

const KEY = 'atd.notify';
const CUSTOM_KEY = 'atd.notify.custom';
const DEFAULTS: NotifyPrefs = { sound: true, desktop: true, soundId: 'bipe', volume: 0.6, customName: null };
const listeners = new Set<() => void>();

export function getPrefs(): NotifyPrefs {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) ?? '{}') };
  } catch {
    return DEFAULTS;
  }
}

export function setPrefs(p: NotifyPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    // armazenamento indisponível: vale só nesta sessão
  }
  listeners.forEach((fn) => fn());
}

export function onPrefsChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Guarda o arquivo de som do usuário (data URL). */
export async function setCustomSound(file: File): Promise<void> {
  if (file.size > MAX_CUSTOM_SOUND_BYTES) throw new Error('O arquivo de som passa de 1 MB');
  const dataUrl = await new Promise<string>((ok, fail) => {
    const r = new FileReader();
    r.onload = () => ok(String(r.result));
    r.onerror = () => fail(r.error);
    r.readAsDataURL(file);
  });
  try {
    localStorage.setItem(CUSTOM_KEY, dataUrl);
  } catch {
    throw new Error('Não foi possível guardar o arquivo neste navegador');
  }
  setPrefs({ ...getPrefs(), soundId: 'custom', customName: file.name });
}

let ctx: AudioContext | null = null;

type Tone = { freq: number; to?: number; at: number; dur: number; type: OscillatorType };

/** Sons prontos (sequências de tons). */
const PRESETS: Record<Exclude<SoundId, 'custom'>, Tone[]> = {
  bipe: [
    { freq: 784, at: 0, dur: 0.14, type: 'sine' },
    { freq: 988, at: 0.16, dur: 0.14, type: 'sine' },
  ],
  sino: [
    { freq: 1318, at: 0, dur: 0.9, type: 'triangle' },
    { freq: 1975, at: 0, dur: 0.5, type: 'sine' },
  ],
  gota: [{ freq: 1400, to: 380, at: 0, dur: 0.22, type: 'sine' }],
  duplo: [
    { freq: 660, at: 0, dur: 0.09, type: 'square' },
    { freq: 660, at: 0.14, dur: 0.09, type: 'square' },
  ],
  suave: [
    { freq: 523, at: 0, dur: 0.35, type: 'sine' },
    { freq: 784, at: 0.22, dur: 0.45, type: 'sine' },
  ],
};

function playTones(tones: Tone[], volume: number): void {
  ctx ??= new AudioContext();
  if (ctx.state === 'suspended') void ctx.resume();
  const peak = Math.max(0.0002, Math.min(1, volume) * 0.4);
  for (const t of tones) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const start = ctx.currentTime + t.at;
    osc.type = t.type;
    osc.frequency.setValueAtTime(t.freq, start);
    if (t.to) osc.frequency.exponentialRampToValueAtTime(t.to, start + t.dur);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + t.dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + t.dur + 0.02);
  }
}

/**
 * Toca o som escolhido. `kind` só muda o som padrão quando nenhum foi escolhido:
 * a fila usa um tom mais grave para se diferenciar de mensagens.
 */
export function playSound(kind: 'message' | 'queue' | 'alert' = 'message', force = false): void {
  const p = getPrefs();
  if (!p.sound && !force) return;
  try {
    if (p.soundId === 'custom') {
      const src = localStorage.getItem(CUSTOM_KEY);
      if (src) {
        const audio = new Audio(src);
        audio.volume = Math.min(1, Math.max(0, p.volume));
        void audio.play().catch(() => undefined);
        return;
      }
    }
    const id = p.soundId === 'custom' ? 'bipe' : p.soundId;
    const tones = id === 'bipe' && kind === 'queue' ? PRESETS.bipe.map((t) => ({ ...t, freq: t.freq * 0.67 })) : PRESETS[id];
    playTones(tones, p.volume);
  } catch {
    // navegador sem áudio: ignora
  }
}

export const desktopSupported = () => typeof window !== 'undefined' && 'Notification' in window;

export async function requestDesktopPermission(): Promise<NotificationPermission> {
  if (!desktopSupported()) return 'denied';
  return Notification.requestPermission();
}

/** Notificação do sistema, só quando a janela não está em uso (senão o aviso na tela basta). */
export function desktopNotify(title: string, body: string, onClick?: () => void, tag?: string): void {
  if (!getPrefs().desktop || !desktopSupported() || Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible' && document.hasFocus()) return;
  try {
    const n = new Notification(title, { body, tag });
    n.onclick = () => {
      window.focus();
      onClick?.();
      n.close();
    };
  } catch {
    // alguns navegadores só permitem notificação por service worker
  }
}
